/**
 * Shapes of the Engagement Center endpoints (docs/plans/engagement-center-contract.md).
 *
 * Aligned with the API (packages/api/src/modules/engagement/**) after the merge on 26.10.2026 —
 * the API is the source of truth. The response keys the screens read are pinned in
 * packages/api/src/modules/engagement/__tests__/contract/response-keys.json and checked on both
 * sides (API contract test, frontend `contract-shapes.test.ts`). Money is always in cents.
 */

/** A number that can be missing — and then says why (contract: "Fehlschlag ≠ Leere"). */
export type MetricStatus = 'ok' | 'not_connected' | 'failed' | 'pending';

export interface Metric<T = number> {
  value: T | null;
  status: MetricStatus;
  reason?: string;
}

export type Channel = 'notification' | 'email' | 'banner';
export type TopicClass = 'transactional' | 'product' | 'offer' | 'news';
export type TenantState = 'healthy' | 'no_pilot' | 'blocked' | 'churn_risk';
export type Segment = 'heavy' | 'normal' | 'occasional' | 'inactive';
export type Risk = 'stable' | 'watch' | 'at_risk';

export const TENANT_STATES: TenantState[] = ['healthy', 'no_pilot', 'blocked', 'churn_risk'];
export const SEGMENTS: Segment[] = ['heavy', 'normal', 'occasional', 'inactive'];
export const CHANNELS: Channel[] = ['notification', 'email', 'banner'];

// ---------------------------------------------------------------- Kunden (activity/)

export interface EngagementOverview {
  /** null until the nightly rollup has run once (then `status` says why). */
  kpis: {
    activeUsers7d: number;
    activeUsersPrev7d: number;
    totalUsers: number;
    /** null when nobody was active in 28 days. */
    stickiness: number | null;
    runningAutopilot: number;
    tenants: number;
    pendingApprovals24h: number;
    unansweredReplies48h: number;
  } | null;
  /** activeUsers is null for days before the first measured day (not zero). */
  trend: { date: string; activeUsers: number | null }[];
  states: Partial<Record<TenantState, number>>;
  annotations: { date: string; label: string; kind: string }[];
  computedAt?: string | null;
  status?: MetricStatus;
  reason?: string;
}

export interface EngagementTenantRow {
  tenantId: string;
  name: string;
  product?: string | null;
  users: number;
  segments?: Partial<Record<Segment, number>>;
  runningCampaigns: number;
  accountStatus?: string;
  /** Human-readable account status (German), e.g. "gesperrt, braucht neue Anmeldung". */
  accountStatusText?: string;
  pendingApprovals24h: number;
  unansweredReplies48h: number;
  activeUsers7d?: number;
  state: TenantState;
  reasons?: string[];
  risk?: Risk;
  riskReasons?: string[];
  isPaying?: boolean;
  mrrCents?: number;
  lastActiveAt?: string | null;
  lastMessageAt?: string | null;
  computedAt?: string;
}

export interface TimelineEntry {
  at: string;
  kind: 'user' | 'autopilot' | 'message' | 'incident';
  text: string;
  userName?: string;
}

// ---------------------------------------------------------------- Nachrichten (messaging/)

export interface Audience {
  kind: 'all' | 'tenants' | 'users' | 'state' | 'segment' | 'trial' | 'paying' | 'former';
  tenantIds?: string[];
  userIds?: string[];
  states?: TenantState[];
  segments?: Segment[];
  trialDayFrom?: number;
  trialDayTo?: number;
  minReplies?: number;
  requireConsent?: 'marketing-email';
}

export interface AudienceEstimate {
  targeted: number;
  withEmail: number;
  excluded: Record<string, number>;
  /** ENGAGEMENT_SEND_ENABLED as the API sees it (only on POST /messages/estimate). */
  sendEnabled?: boolean;
}

export interface InlineContent {
  title: string;
  body: string;
  ctaLabel?: string;
  ctaUrl?: string;
  subject?: string;
}

export interface PreviewRequest {
  templateKey?: string;
  content?: InlineContent;
  channels: Channel[];
  variableValues?: Record<string, string>;
  userId?: string;
}

export interface MessagePreview {
  notification?: { title: string; body: string; ctaLabel?: string; ctaUrl?: string };
  email?: { subject: string; html: string; text: string };
  banner?: { title?: string; body: string };
  missing: string[];
  language?: 'de' | 'en' | 'fr';
  /** Whose data filled the variables. */
  recipient?: { userId: string; tenantId: string; name: string };
}

export interface ComposeRequest {
  topic: string;
  topicClass: TopicClass;
  channels: Channel[];
  audience: Audience;
  templateKey?: string;
  content?: InlineContent;
  variableValues?: Record<string, string>;
  delivery?: 'window' | 'immediate';
  testId?: string;
  dryRun?: boolean;
}

export type MessageStatus = 'scheduled' | 'running' | 'completed' | 'failed' | 'awaiting_approval' | 'rejected' | 'dry_run';

export interface SendResult {
  messageId: string | null;
  deduplicated?: boolean;
  status: MessageStatus;
  estimate?: AudienceEstimate;
}

export interface MessageAggregate {
  targeted: number;
  sent?: Partial<Record<Channel, number>>;
  skipped?: Record<string, number>;
  failed?: number;
  pending?: number;
  delivered?: number;
  opened?: number;
  clicked?: number;
  returned?: number;
  completed?: number;
  bounced?: number;
  unsubscribed?: number;
  complained?: number;
}

export interface MessageActor {
  userId: string;
  name?: string;
  via: 'ui' | 'mcp';
}

export interface EngagementMessage {
  _id: string;
  topic: string;
  topicClass?: TopicClass;
  channels: Channel[];
  templateKey?: string;
  audience?: Audience;
  /** Who sent it; for `awaiting_approval` this is also the requester — the approver must be someone else. */
  source?: { type: 'manual' | 'automation' | 'system' | 'test'; refId?: string; actor?: MessageActor };
  status: MessageStatus;
  delivery?: 'window' | 'immediate';
  scheduledFor?: string;
  createdAt?: string;
  aggregate?: MessageAggregate;
  killSwitch?: { triggeredAt: string; reason: string };
  error?: string;
}

/** engagement_approvals row, only in the message detail. */
export interface MessageApproval {
  requestedBy: MessageActor;
  approvedBy?: MessageActor;
  status: 'open' | 'approved' | 'rejected';
  decidedAt?: string;
  createdAt?: string;
}

export interface MessageDelivery {
  _id?: string;
  tenantId: string;
  userId: string;
  /** Not sent by the API; the UI shows `email` (set on every delivery the hub wrote), else userId. */
  userName?: string;
  email?: string;
  channel: Channel;
  status: string;
  reason?: string;
  sentAt?: string;
  openedAt?: string;
  clickedAt?: string;
  returnedAt?: string;
  completedAt?: string;
}

/**
 * GET /messages/:id answers `{ message, deliveries, artifacts, approval }`; the client flattens
 * `message` into the top level so the dialog reads `detail.topic` like an outbox row.
 */
export interface MessageDetail extends EngagementMessage {
  deliveries: MessageDelivery[];
  artifacts: { deliveryId?: string; channel: Channel; to: string; subject?: string; html?: string; text?: string; sentAt?: string }[];
  approval: MessageApproval | null;
}

export interface Paged<T> {
  items: T[];
  total?: number;
  page?: number;
}

// ---------------------------------------------------------------- Vorlagen

export interface TemplateChannelText {
  title?: string;
  subject?: string;
  body: string;
  ctaLabel?: string;
}

export interface TemplateVariable {
  name: string;
  source: 'deterministic' | 'manual' | 'llm';
  path?: string;
  instruction?: string;
}

export interface EngagementTemplate {
  key: string;
  name: string;
  topicClass: TopicClass;
  system?: boolean;
  active: boolean;
  variants: Partial<Record<'de' | 'en' | 'fr', { notification?: TemplateChannelText; email?: TemplateChannelText }>>;
  variables: TemplateVariable[];
  ctaUrl?: string;
  recipientBenefit: string;
  lastTestedAt?: string | null;
  /** From the API: the last test covered exactly the current wording (activation needs it). */
  testedCurrentWording?: boolean;
}

// ---------------------------------------------------------------- Einstellungen

export interface EngagementSettings {
  timeSaved: { profileReviewMin: number; connectionRequestMin: number; followUpMin: number; inmailMin: number };
  caps: { emailPerDay: number; emailPerWeek: number; notificationPerDay: number; cooldownHours: number };
  sunsetAfterUnopened: number;
  /** Read-only here (GET); PUT accepts it but the UI does not edit it. */
  killSwitch?: { unsubscribeRate: number; complaintRate: number; minEmails: number };
  /** The two env switches; GET /settings sends them top-level and under `flags`. Never sent back on PUT. */
  sendEnabled?: boolean;
  automationsEnabled?: boolean;
  flags?: { sendEnabled?: boolean; automationsEnabled?: boolean };
}

/** Settings PUT body: partial, may only tighten the budget (the API rejects anything looser). */
export type EngagementSettingsUpdate = Partial<Pick<EngagementSettings, 'timeSaved' | 'caps' | 'sunsetAfterUnopened' | 'killSwitch'>>;

export interface KillSwitchIncident {
  messageId: string;
  topic: string;
  source?: EngagementMessage['source'];
  triggeredAt?: string;
  reason?: string;
}

export interface EngagementInsights {
  days?: number;
  /** { targeted, sent, delivered, opened, clicked, returned, completed } */
  funnel: Record<string, number>;
  /** 5 buckets: 0, 1, 2, 3, 4+ non-transactional sends per user in the last 7 days. */
  sendsPerUserWeek: number[];
  /** Skip reasons without the `skipped_` prefix. */
  skips: Record<string, number>;
  unsubscribeRate: Metric;
  complaintRate?: Metric;
  emails?: number;
  lowTouch: { value: number; of: number };
  killSwitchIncidents: { count: number; items: KillSwitchIncident[] };
}

// ---------------------------------------------------------------- Automationen, Aktionen, Bindung

export interface EngagementAutomation {
  /** = key */
  _id?: string;
  key: string;
  name: string;
  description?: string;
  enabled: boolean;
  priority: number;
  trigger?: string | Record<string, unknown>;
  conditions?: Record<string, unknown>;
  templateKey?: string;
  channels?: Channel[];
  lastRunAt?: string | null;
  lastRunStats?: { evaluated: number; wouldSend: number; sent: number; skipped: number; skippedReasons?: Record<string, number>; shadow?: boolean };
  killSwitch?: { triggeredAt: string; reason: string } | null;
  /** Seeded row whose definition still exists in code. */
  known?: boolean;
}

/** GET /automations → `{ automationsEnabled, items }`. */
export interface AutomationsList {
  automationsEnabled: boolean;
  items: EngagementAutomation[];
}

export type DryRunCandidateStatus = 'would_send' | 'sent' | 'already_sent' | 'lower_priority' | 'control_group' | 'failed';

export interface AutomationDryRun {
  key: string;
  enabled: boolean;
  automationsEnabled: boolean;
  conditions: Record<string, unknown>;
  /** Candidates per status; `would_send` is what a real run would send now. */
  counts: Partial<Record<DryRunCandidateStatus, number>>;
  audience: {
    tenantId: string;
    userId: string;
    step?: number;
    period?: string;
    templateKey?: string;
    channels?: Channel[];
    status: DryRunCandidateStatus;
    idempotencyKey?: string;
  }[];
  note?: string;
}

export interface EngagementPromotion {
  _id: string;
  name: string;
  discount: { percentOff: number; durationMonths: number };
  audience: Audience | Record<string, unknown>;
  endsAfterDays: number;
  maxRedemptions: number;
  controlGroupPercent: number;
  code?: string;
  expiresAt?: string;
  stripeCouponId?: string;
  stripePromotionCodeId?: string;
  status: 'draft' | 'running' | 'ended';
  /** Running but past expiresAt (then status is reported as 'ended'). */
  expired?: boolean;
  stats?: {
    delivered: number;
    redeemed: number;
    revenueCents: number;
    control?: { size: number; purchased: number };
    status?: MetricStatus;
    reason?: string;
  };
}

/**
 * The draft as the screen edits it. The API body is flat (`percentOff`, `durationMonths`) —
 * `createPromotion` in API/engagement.ts converts. API limits: percentOff 1–60, durationMonths 1–12,
 * endsAfterDays 1–60, maxRedemptions 1–1000, controlGroupPercent 0–50, name 3–80 characters.
 */
export interface CreatePromotionRequest {
  name: string;
  discount: { percentOff: number; durationMonths: number };
  audience: Audience;
  endsAfterDays: number;
  maxRedemptions: number;
  controlGroupPercent: number;
  dryRun: boolean;
}

/** POST /promotions → dry run `{ dryRun: true, preview }` or `{ dryRun: false, promotion }`. */
export interface PromotionPreview {
  dryRun: boolean;
  preview?: {
    name: string;
    code: string;
    expiresAt: string;
    discount: { percentOff: number; durationMonths: number };
    maxRedemptions: number;
    controlGroupPercent: number;
    audience: Audience;
    stripe?: Record<string, unknown>;
    estimate: AudienceEstimate | null;
    estimateReason?: string;
    text: string;
  };
  promotion?: EngagementPromotion;
}

export interface AtRiskRow {
  tenantId: string;
  name: string;
  /** Missing when tenant_health has no MRR for this tenant. */
  mrrCents?: number;
  risk: Risk;
  /** tenant_health reasons plus subscription facts (cancellation pending, payment open). */
  reasons: string[];
}

export interface EngagementRetention {
  payingTenants: number;
  mrrCents: Metric;
  churnRateMonth: Metric;
  nrr: Metric;
  atRisk: AtRiskRow[];
  /** 'not_connected' while tenant_health is missing — then atRisk only has subscription facts. */
  atRiskStatus?: MetricStatus;
  cohorts: { month: string; size?: number; retention: (number | null)[] }[];
  reactivation: {
    formerTenants: number;
    formerContacted: number;
    reactivated: number;
    cancelIntents: number;
    paused: number;
    downgraded: number;
    reviewRequested: number;
    kept: number;
    cancelled: number;
  };
  churnReasons: Record<string, number>;
  computedAt?: string;
}

export type TrialStage = 'registered' | 'trial' | 'activated' | 'first_reply' | 'trial_expired';

/** GET /trial-pipeline → `{ items, runningPromotion, computedAt }`. */
export interface TrialPipelineRow {
  tenantId: string;
  name: string;
  stage: TrialStage;
  day: number | null;
  lengthDays: number | null;
  daysLeft: number | null;
  startsAt: string | null;
  endsAt: string | null;
  activated: boolean;
  /** Counts of the trial period, or a failed Metric when they could not be computed. */
  results: ({ requests?: number; accepted?: number; replies?: number; conversations?: number; hoursSaved?: number } & Partial<Metric>) | null;
  marketingConsent: boolean;
  controlGroup: boolean | null;
}

export interface TrialPipeline {
  items: TrialPipelineRow[];
  runningPromotion: { id: string; name: string; code?: string; expiresAt: string } | null;
  computedAt?: string;
}

// ---------------------------------------------------------------- Wachstum, Umsatz

export interface FunnelRow {
  source: string;
  visits?: number;
  spend: Metric;
  registrations: number;
  activated: number;
  firstReply: number;
  purchases: number;
  revenueCents?: number;
  costPerCustomer: Metric;
}

/** Stripe revenue in the window vs. what the sources account for. */
export interface ControlSum {
  revenueEventsCents: number;
  attributedCents: number;
  differenceCents: number;
  ok: boolean;
  reason?: string;
}

/** GET /growth/funnel → `{ days, rows, controlSum, platforms, computedAt }`. */
export interface GrowthFunnel {
  rows: FunnelRow[];
  controlSum?: ControlSum;
  platforms?: Record<'meta' | 'google', Metric<true>>;
}

export interface RoasRow {
  platform: 'meta' | 'google' | string;
  campaignId: string;
  name: string;
  spendCents: number;
  registrations?: number;
  activatedTrials?: number;
  purchases: number;
  revenueCents: number;
  /** null when nothing was spent. */
  roasToDate: number | null;
  roas12m: number | null;
  paybackMonths: number | null;
}

/** GET /growth/roas — rows come as `campaigns`; the client exposes them as `rows`. */
export interface GrowthRoas {
  rows: RoasRow[];
  status?: MetricStatus;
  reason?: string;
  totals?: { spend: Metric; revenueCents: number; roasToDate: number | null };
  unmatched?: { registrations: number; purchases: number; revenueCents: number };
  withoutAds?: { registrations: number; purchases: number; revenueCents: number };
  controlSum?: ControlSum;
}

/** GET /growth/experiments answers `[{ experimentId, …, variants: [...] }]`; the client flattens. */
export interface ExperimentVariantRow {
  experimentId: string;
  name?: string;
  variant: string;
  variantLabel?: string;
  views: number;
  /** null without views. */
  ctaRate: number | null;
  registrations: number;
  activated: number;
  purchases: number;
  probabilityBest: number | null;
  decision: 'undecided' | 'winner' | 'loser';
  daysRunning: number;
  requiredVisitors: number;
}

// ---------------------------------------------------------------- Kunde

export interface CustomerNotification {
  _id: string;
  kind: string;
  title: string;
  body: string;
  ctaLabel?: string;
  ctaUrl?: string;
  readAt?: string | null;
  clickedAt?: string | null;
  createdAt: string;
}

export interface NotificationList {
  items: CustomerNotification[];
  unread: number;
}

export type PreferenceTopic = 'account' | 'campaigns' | 'daily_report' | 'news' | 'offers';
export const PREFERENCE_TOPICS: PreferenceTopic[] = ['account', 'campaigns', 'daily_report', 'news', 'offers'];

/**
 * GET /notifications/preferences → `{ matrix, fixed, consent: { marketingEmail }, updatedAt }`;
 * the client copies `consent.marketingEmail` to `marketingEmailConsent`.
 */
export interface NotificationPreferences {
  matrix: Record<PreferenceTopic, { notification: boolean; email: boolean }>;
  fixed?: PreferenceTopic[];
  consent?: { marketingEmail: boolean };
  /** Whether the user gave the marketing-email consent (offers.email needs it). */
  marketingEmailConsent?: boolean;
  updatedAt?: string | null;
}

export type ResultsPeriod = 'trial' | 'month' | 'lifetime';

export interface SubscriptionResults {
  requests: number;
  accepted: number;
  replies: number;
  conversations: number;
  inmails?: number;
  followUps?: number;
  hoursSaved: number;
  calculation: string;
  /** The API sends the period name; the range comes as from/to. */
  period: ResultsPeriod | { kind?: ResultsPeriod; from?: string; to?: string; months?: number };
  from?: string | null;
  to?: string;
  /** 'pending' e.g. for period=trial without a trial ("Kein Testzeitraum gefunden"). */
  status?: 'ok' | 'pending';
  reason?: string;
}

export type CancelReason = 'role_filled' | 'too_expensive' | 'no_results' | 'other';
export const CANCEL_REASONS: CancelReason[] = ['role_filled', 'too_expensive', 'no_results', 'other'];

export interface CancelOffer {
  kind: 'pause' | 'downgrade' | 'review' | 'none';
  text: string;
}
